/**
 * DIKDOC HUB — Frontend environment diagnostics
 *
 * Logs all VITE_* env vars in dev mode + warns in production if any are missing.
 * Helps debug "Failed to fetch" issues from the user's perspective.
 */

const REQUIRED_VARS = [
  "VITE_SUPABASE_URL",
  "VITE_SUPABASE_ANON_KEY",
  "VITE_API_URL",
] as const;

const OPTIONAL_VARS = [
  "VITE_APP_URL",
  "VITE_APP_VERSION",
  "VITE_API_DEGRADED",
  "VITE_ENABLE_BILLING",
  "VITE_ENABLE_FHIR",
  "VITE_ENABLE_LIMS",
  "VITE_ENABLE_PACS",
  "VITE_ENABLE_PHARMACY",
] as const;

export interface EnvDiagnostics {
  ok: boolean;
  missing: string[];
  present: Record<string, string>;
  mode: "production" | "preview" | "development";
  appVersion: string;
}

function mask(value: string | undefined, keepLast = 4): string {
  if (!value) return "(empty)";
  if (value.length <= keepLast + 4) return "***";
  return `${value.slice(0, 6)}...${value.slice(-keepLast)}`;
}

export function runDiagnostics(): EnvDiagnostics {
  const missing: string[] = [];
  const present: Record<string, string> = {};
  for (const key of REQUIRED_VARS) {
    const v = import.meta.env[key];
    if (!v || String(v).trim() === "") {
      missing.push(key);
    } else {
      present[key] = key.includes("KEY") || key.includes("SECRET") ? mask(String(v)) : String(v);
    }
  }
  for (const key of OPTIONAL_VARS) {
    const v = import.meta.env[key];
    if (v) present[key] = String(v);
  }

  const mode: EnvDiagnostics["mode"] = import.meta.env.PROD
    ? "production"
    : import.meta.env.MODE === "preview"
      ? "preview"
      : "development";

  return {
    ok: missing.length === 0,
    missing,
    present,
    mode,
    appVersion: String(import.meta.env["VITE_APP_VERSION"] ?? "unknown"),
  };
}

/**
 * Display a small banner in the UI when env vars are missing.
 * This makes "Failed to fetch" issues user-visible instead of silent.
 */
export function renderDiagnosticsBanner(diag: EnvDiagnostics): string | null {
  if (diag.ok) return null;
  return `⚠ Configuration incomplete: missing ${diag.missing.join(", ")}. Some features may not work.`;
}

// Run on module load — logs to console for developer debugging
if (typeof window !== "undefined") {
  const diag = runDiagnostics();
  // eslint-disable-next-line no-console
  console.log("[DIKDOC env]", { mode: diag.mode, version: diag.appVersion, present: diag.present, missing: diag.missing });
  if (!diag.ok) {
    // eslint-disable-next-line no-console
    console.warn(`[DIKDOC env] Missing required vars: ${diag.missing.join(", ")}`);
  }
}
